import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { twMerge } from 'tailwind-merge';
import { faLock, faSend } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { faUserCircle } from '@soundxyz/font-awesome/pro-solid-svg-icons/index.mjs';
import { Avatar } from '@components/basics/Images/Avatar';
import { FeedPostModal } from '@components/modals/FeedPostModal';
import { UnlockFeedModal } from '@components/modals/FeedPostModal/UnlockFeedModal';
import { useAuth } from '@context/auth/base';
import { useConnectedAuth } from '@context/auth/connected';
import { MediaQuery } from '@context/mediaQuery';
import { ModalType, useModal } from '@context/modal';
import { useBackendUserPersistedData } from '@hooks/useBackendUserPersistedData';
import { trackClickEvent } from '@lib/analytics';

export function FeedPostInput({ className = '' }: { className?: string }) {
  const { authUser, authToken, isHydrated } = useAuth();
  const { openModal } = useModal();
  const { login } = useConnectedAuth();

  const { hasFeedAccess } = useBackendUserPersistedData();

  const hasPostingAccess = hasFeedAccess.type === 'has-access';

  const { tablet } = MediaQuery.useContainer();

  const inputPlaceholder = useMemo(() => {
    if (authToken) {
      return `What sound is on your mind${
        authUser?.displayName ? `, ${authUser.displayName}...` : '?'
      }`;
    } else {
      return 'Sign in to post...';
    }
  }, [authToken, authUser?.displayName]);

  const showTooltip = useMemo(() => {
    return Boolean(authToken) && !hasPostingAccess;
  }, [authToken, hasPostingAccess]);

  const buttonContent = useMemo(
    () => (
      <button
        className="relative flex flex-1 items-center rounded-md bg-base100 px-3 py-2 text-base-l text-neutral500 ring-inset ring-base300 hover:bg-neutral50 hover:ring-1"
        onClick={() => {
          if (!authToken) {
            login();
            return;
          }

          if (hasPostingAccess) {
            trackClickEvent({
              event: 'Create Post Clicked',
              properties: {
                sourceComponent: 'Feed Post Input',
              },
            });
            openModal(ModalType.FEED_POST, {
              body: <FeedPostModal key="post-input" sourceLocation="Feed Post Input" />,
            });
          } else {
            openModal(ModalType.UNLOCK_FEED_POST, {
              body: <UnlockFeedModal key="unlock-feed-post" sourceLocation="Feed Post Input" />,
            });
          }
        }}
      >
        {isHydrated && (
          <>
            <div className="line-clamp-1 flex-1 text-left">{inputPlaceholder}</div>
            <div className=" flex aspect-square h-full w-min flex-00auto items-center justify-center rounded-full bg-base800 opacity-30">
              {!showTooltip ? (
                <FontAwesomeIcon icon={faSend} className="text-base-xs text-white md:text-base-m" />
              ) : (
                <FontAwesomeIcon icon={faLock} className="text-base-xs text-white md:text-base-m" />
              )}
            </div>
          </>
        )}
      </button>
    ),
    [showTooltip, isHydrated, inputPlaceholder, authToken, hasPostingAccess, login, openModal],
  );

  return (
    <div className={twMerge('flex w-full gap-2 md:gap-4', className)}>
      <div className="h-10 w-10 rounded-full bg-neutral500 md:h-12 md:w-12">
        {authUser ? (
          <Avatar
            walletAddress={authUser.publicAddress}
            username={authUser.username}
            webappUri={authUser.webappUri}
            size={tablet ? 48 : 40}
            src={authUser.avatar?.url}
            borderRadius={999}
          />
        ) : (
          <div className="flex items-center justify-center bg-white">
            <FontAwesomeIcon
              icon={faUserCircle}
              className="text-avatar text-neutral500 md:text-avatar-md"
            />
          </div>
        )}
      </div>

      {buttonContent}
    </div>
  );
}
